// Location: src/services/payment/lipanaMpesaService.js

import axios from 'axios';

// Backend API URL - backend handles Lipana API calls to avoid CORS issues
const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

console.log('🔗 Backend URL:', BACKEND_URL);

/**
 * Format phone number to M-Pesa format (254XXXXXXXXX)
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Remove all spaces, dashes, and special characters
  let formatted = phone.replace(/[\s\-()]/g, '');
  
  // Remove leading zeros and plus sign
  if (formatted.startsWith('0')) {
    formatted = '254' + formatted.substring(1);
  } else if (formatted.startsWith('+254')) {
    formatted = formatted.substring(1);
  } else if (formatted.startsWith('254')) {
    // Already in correct format
    formatted = formatted;
  } else if (formatted.startsWith('+')) {
    formatted = formatted.substring(1);
  }
  
  return formatted;
};

/**
 * Validate M-Pesa payment data before sending
 */
export const validateMpesaPaymentData = (paymentData) => {
  const { phoneNumber, amount, orderId } = paymentData;
  
  // Validate phone number
  if (!phoneNumber) {
    return { valid: false, error: 'Phone number is required' };
  }
  
  const formattedPhone = formatPhoneNumber(phoneNumber);
  
  // Check if phone number is valid Kenyan number
  if (!formattedPhone.match(/^254[17]\d{8}$/)) {
    return { 
      valid: false, 
      error: 'Please enter a valid Kenyan M-Pesa number (e.g., 0712345678)' 
    };
  }
  
  // Validate amount
  if (!amount || amount < 1) {
    return { valid: false, error: 'Amount must be at least KES 1' };
  }
  
  if (amount > 250000) {
    return { valid: false, error: 'Amount cannot exceed KES 250,000' };
  }
  
  // Validate order ID
  if (!orderId) {
    return { valid: false, error: 'Order ID is required' };
  }
  
  return { valid: true };
};

/**
 * Initiate M-Pesa STK Push payment via Lipana
 * Calls backend which proxies to Lipana API to avoid CORS issues
 */
export const initiateLipanaMpesaPayment = async (paymentData) => {
  try {
    const { phoneNumber, amount, orderId, accountReference, description } = paymentData;
    
    // Validate data
    const validation = validateMpesaPaymentData({ phoneNumber, amount, orderId });
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error
      };
    }
    
    const formattedPhone = formatPhoneNumber(phoneNumber);
    
    console.log('🚀 Initiating Lipana M-Pesa payment via backend:', {
      phone: formattedPhone,
      amount,
      orderId,
      backendUrl: BACKEND_URL
    });
    
    // Call backend endpoint which proxies to Lipana
    const response = await axios.post(`${BACKEND_URL}/api/lipana/initiate-payment`, {
      phoneNumber: formattedPhone,
      amount: parseFloat(amount),
      orderId: orderId,
      accountReference: accountReference || `ALLARTWEB-${orderId}`,
      description: description || `AllArtWeb Order #${orderId}`
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    console.log('📥 Backend response:', response.status, response.data);
    
    if (response.data.success) {
      console.log('✅ M-Pesa STK Push initiated successfully');
      return {
        success: true,
        checkoutRequestId: response.data.checkoutRequestId,
        responseCode: response.data.responseCode,
        message: response.data.message || 'M-Pesa prompt sent to your phone',
        timestamp: new Date().toISOString()
      };
    } else {
      console.error('❌ STK Push failed:', response.data);
      return {
        success: false,
        error: response.data.error || 'Failed to initiate M-Pesa payment',
        responseCode: response.data.responseCode,
        details: response.data
      };
    }
    
  } catch (error) {
    console.error('❌ Payment initiation error:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    
    // Handle different error scenarios
    if (error.response?.status === 500) {
      return {
        success: false,
        error: 'Payment service not configured. Please contact support.'
      };
    } else if (error.code === 'ECONNREFUSED') {
      return {
        success: false,
        error: 'Unable to connect to payment server. Make sure backend is running on ' + BACKEND_URL
      };
    } else if (error.response?.data?.error) {
      return {
        success: false,
        error: error.response.data.error
      };
    } else if (error.request && !error.response) {
      return {
        success: false,
        error: 'Network error. Please check your internet connection.'
      };
    } else {
      return {
        success: false,
        error: error.message || 'Payment processing error. Please try again.'
      };
    }
  }
};

/**
 * Check M-Pesa payment status
 * Poll this endpoint to check if payment was successful
 */
export const checkMpesaPaymentStatus = async (checkoutRequestId) => {
  try {
    if (!checkoutRequestId) {
      return {
        success: false,
        error: 'Checkout request ID is required'
      };
    }
    
    console.log(`📊 Checking payment status for: ${checkoutRequestId}`);
    
    const response = await axios.get(
      `${BACKEND_URL}/api/mpesa/payment-status/${checkoutRequestId}`
    );
    
    console.log(`📥 Status response:`, response.data);
    
    if (response.data.success) {
      const { result_code, result_desc, mpesa_receipt_number, amount } = response.data.data;
      
      return {
        success: true,
        status: result_code === '0' ? 'completed' : result_code ? 'failed' : 'pending',
        resultCode: result_code,
        resultDesc: result_desc,
        mpesaReceiptNumber: mpesa_receipt_number,
        amount: amount,
        data: response.data.data
      };
    }
    
    return {
      success: false,
      error: 'Unable to check payment status'
    };
    
  } catch (error) {
    console.error('❌ Status check error:', error.message);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to check payment status'
    };
  }
};

/**
 * Get user transactions from Firestore via backend
 */
export const getUserTransactions = async (userId, limit = 50) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}/api/mpesa/transactions/user/${userId}`,
      {
        params: { limit }
      }
    );
    
    if (response.data.success) {
      return {
        success: true,
        transactions: response.data.data
      };
    }
    
    return {
      success: false,
      error: 'Failed to fetch transactions'
    };
    
  } catch (error) {
    console.error('Get transactions error:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to fetch transactions'
    };
  }
};

/**
 * Poll payment status until completion or timeout
 * Use this to wait for user to complete M-Pesa payment
 */
export const pollPaymentStatus = async (checkoutRequestId, options = {}) => {
  const {
    maxAttempts = 30, // 30 attempts
    interval = 6000, // 6 seconds between attempts (total 3 minutes)
    onProgress = null // Callback for progress updates
  } = options;
  
  return new Promise((resolve, reject) => {
    let attempts = 0;
    
    const pollInterval = setInterval(async () => {
      attempts++;
      
      if (onProgress) {
        onProgress({ attempts, maxAttempts, status: 'polling' });
      }
      
      try {
        const result = await checkMpesaPaymentStatus(checkoutRequestId);
        
        console.log(`📊 Poll attempt ${attempts}/${maxAttempts}:`, result);
        
        if (result.success && result.status === 'completed') {
          // Payment successful - stop polling immediately
          console.log('✅ Payment completed! Stopping polling.');
          clearInterval(pollInterval);
          resolve({
            success: true,
            status: 'completed',
            ...result
          });
          return; // Exit to prevent further polling
        } 
        
        if (result.success && result.status === 'failed') {
          // Payment failed - stop polling
          console.log('❌ Payment failed! Stopping polling.');
          clearInterval(pollInterval);
          resolve({
            success: false,
            status: 'failed',
            error: result.resultDesc || 'Payment failed'
          });
          return; // Exit to prevent further polling
        }
        
        if (result.success && result.status === 'cancelled') {
          // Payment cancelled - stop polling
          console.log('⛔ Payment cancelled! Stopping polling.');
          clearInterval(pollInterval);
          resolve({
            success: false,
            status: 'cancelled',
            error: 'Payment was cancelled'
          });
          return; // Exit to prevent further polling
        }
        
        // Check timeout and stop if max attempts reached
        if (attempts >= maxAttempts) {
          console.log('⏱️ Max polling attempts reached. Stopping polling.');
          clearInterval(pollInterval);
          resolve({
            success: false,
            status: 'timeout',
            error: 'Payment verification timed out. Please check your M-Pesa messages.'
          });
          return; // Exit to prevent further polling
        }
        
      } catch (error) {
        console.error('❌ Polling error on attempt', attempts, ':', error.message);
        // Continue polling on error, but log it
      }
    }, interval);
  });
};

/**
 * Get transaction by ID
 */
export const getTransactionById = async (transactionId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}/api/mpesa/transaction/${transactionId}`
    );
    
    if (response.data.success) {
      return {
        success: true,
        transaction: response.data.data
      };
    }
    
    return {
      success: false,
      error: 'Transaction not found'
    };
    
  } catch (error) {
    console.error('Get transaction error:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to fetch transaction'
    };
  }
};

/**
 * Get order payment status
 */
export const getOrderPaymentStatus = async (orderId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}/api/orders/${orderId}/payment-status`
    );
    
    if (response.data.success) {
      return {
        success: true,
        paymentStatus: response.data.data.paymentStatus,
        transaction: response.data.data.transaction
      };
    }
    
    return {
      success: false,
      error: 'Order not found'
    };
    
  } catch (error) {
    console.error('Get order payment status error:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to fetch order status'
    };
  }
};

export default {
  formatPhoneNumber,
  validateMpesaPaymentData,
  initiateLipanaMpesaPayment,
  checkMpesaPaymentStatus,
  pollPaymentStatus,
  getUserTransactions,
  getTransactionById,
  getOrderPaymentStatus
};